/**
 * The stop page's map — `docs/activity.md` §10.3, F23, Board 14 A.
 *
 * 80 px of the place with its pin: a **non-interactive MapLibre view**
 * (`interactive: false`), no controls, no scale. The same pieces the property
 * map and folder 48's address map are built from — the imagery session from
 * `lib/map-provider`, MapLibre by dynamic `import()` with its worker URL from
 * Vite — so the library stays its own chunk, fetched the first time a stop is
 * opened. The road map, as Board 14 draws it; where there is no imagery the
 * page's own fill, with the pin still on it. The provider's credit stays,
 * small, because the tiles' licence asks for it.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { Map as MapLibreMap, StyleSpecification } from 'maplibre-gl'
import { tokenColour } from '../../lib/token-colour.js'
import { imagerySession, type ImagerySession } from '../../lib/map-provider.js'

/** Close enough to see the street, far enough to see the turns before it. */
const ZOOM = 15

export const STOP_MAP_HEIGHT = 80

export function StopMap({ latitude, longitude }: { latitude: number; longitude: number }) {
  const host = useRef<HTMLDivElement>(null)
  const [pinNode] = useState(() => (typeof document === 'undefined' ? null : document.createElement('div')))
  const [session, setSession] = useState<ImagerySession | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let live = true
    imagerySession()
      .then((got) => { if (live) setSession(got) })
      .catch(() => { if (live) setFailed(true) })
    return () => { live = false }
  }, [])

  useEffect(() => {
    if ((!session && !failed) || !host.current || !pinNode) return undefined
    let cancelled = false
    let made: MapLibreMap | null = null
    void (async () => {
      const maplibre = await import('maplibre-gl')
      await import('maplibre-gl/dist/maplibre-gl.css')
      maplibre.setWorkerUrl((await import('maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url')).default)
      if (cancelled || !host.current) return
      made = new maplibre.Map({
        container: host.current,
        style: styleOf(session),
        center: [longitude, latitude],
        zoom: ZOOM,
        interactive: false,
        attributionControl: false,
        renderWorldCopies: false,
        pixelRatio: Math.min(2, window.devicePixelRatio || 1),
      })
      /* The pin's tip sits 13 px below the middle, so its head is in the middle of the strip (Board 14 A). */
      made.setPadding({ top: 26, bottom: 0, left: 0, right: 0 })
      new maplibre.Marker({ element: pinNode, anchor: 'bottom' }).setLngLat([longitude, latitude]).addTo(made)
    })()
    return () => { cancelled = true; made?.remove() }
  }, [session, failed, pinNode, latitude, longitude])

  return (
    <div className="relative overflow-hidden rounded-xl border border-line bg-surface-group"
      style={{ height: STOP_MAP_HEIGHT + 2 }}>
      <div ref={host} className="absolute inset-0 h-full w-full" />
      {pinNode && createPortal(<Pin />, pinNode)}
      {session?.attribution && (
        <span className="absolute bottom-1 left-2 text-[9px] text-text-faint">© {session.attribution}</span>
      )}
    </div>
  )
}

/** Board 14's pin: the green drop with a white dot, in a soft halo. */
function Pin() {
  return (
    <span className="relative block h-[35px] w-6">
      <span aria-hidden className="absolute -left-2.5 -top-0.5 block h-11 w-11 rounded-full bg-accent/[.12]" />
      <svg aria-hidden viewBox="-1 -1 24 35" width="24" height="35" className="relative block text-accent">
        <path d="M11 32 C2 20 0 15 0 11 A11 11 0 1 1 22 11 C22 15 20 20 11 32z"
          fill="currentColor" stroke="rgb(var(--color-surface))" strokeWidth="2" />
        <circle cx="11" cy="11" r="4" fill="rgb(var(--color-surface))" />
      </svg>
    </span>
  )
}

function styleOf(session: ImagerySession | null): StyleSpecification {
  if (!session) {
    return {
      version: 8 as const,
      sources: {},
      layers: [{ id: 'plain', type: 'background' as const, paint: { 'background-color': tokenColour('surface-group') } }],
    }
  }
  return {
    version: 8 as const,
    sources: {
      base: { type: 'raster' as const, tiles: session.tiles.map, tileSize: 256, maxzoom: Math.min(20, session.maxZoom) },
    },
    layers: [{ id: 'base', type: 'raster' as const, source: 'base' }],
  }
}
